import type { ThemeMode } from "@/composables/theme";
import { computed, defineComponent } from "vue";
import Dropdown from "@/components/primitives/dropdown/Dropdown";
import IconButton from "@/components/ui/icon-button/IconButton";
import Icon from "@/components/ui/icon/Icon";
import { useTheme } from "@/composables/theme";

import { $t } from "@/services/i18n";

interface Option {
  value: ThemeMode;
  label: string;
  icon: string;
}

export default defineComponent({
  name: "ThemeSwitcher",
  setup() {
    const { setMode, theme, mode } = useTheme();

    const icon = computed(() => {
      switch (theme.value) {
        case "dark":
          return "lucide:moon";
        default:
          return "lucide:sun";
      }
    });

    const options = computed(
      () =>
        [
          {
            value: "light",
            label: $t("light"),
            icon: "lucide:sun",
          },
          {
            value: "dark",
            label: $t("dark"),
            icon: "lucide:moon",
          },
          {
            value: "system",
            label: $t("system"),
            icon: "lucide:tv",
          },
        ] as Option[]
    );

    function isActive(option: (typeof options.value)[number]) {
      return option.value === mode.value;
    }

    return () => (
      <Dropdown.Root>
        <Dropdown.Trigger asChild>
          <IconButton
            label="Toggle theme"
            icon={icon.value}
            variant="ghost"
            size="sm"
            class="shrink-0"
          />
        </Dropdown.Trigger>
        <Dropdown.Content avoidCollisions sideOffset={8} align="end">
          <Dropdown.Group>
            {options.value.map((option) => (
              <Dropdown.Item class="w-32" key={option.value} onSelect={() => setMode(option.value)}>
                <Icon icon={option.icon} class="mr-2.5 h-4 w-4 flex-none" />
                {option.label}
                {isActive(option) && <Icon icon="radix-icons:dot-filled" class="ml-auto h-4 w-4" />}
              </Dropdown.Item>
            ))}
          </Dropdown.Group>
        </Dropdown.Content>
      </Dropdown.Root>
    );
  },
});
